import {
  ENVIRONMENT_INITIALIZER,
  InjectionToken,
  PLATFORM_ID,
  Provider,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Platform } from '@angular/cdk/platform';
import { MatTooltip } from '@angular/material/tooltip';
import { Capacitor } from '@capacitor/core';

export const TOOLTIP_MOBILE_DETECTOR = new InjectionToken<() => boolean>(
  'tooltip-mobile-detector',
  {
    factory: () => {
      const platform = inject(Platform);
      const platformId = inject(PLATFORM_ID);
      return () => detectMobilePlatform(platform, platformId);
    },
  }
);

type TooltipPatchState = {
  applied: boolean;
  originalDisabled?: PropertyDescriptor;
  originalShow?: MatTooltip['show'];
  originalToggle?: MatTooltip['toggle'];
};

const TOOLTIP_PATCH_STATE: TooltipPatchState = {
  applied: false,
};

function detectMobilePlatform(platform: Platform, platformId: object): boolean {
  if (!isPlatformBrowser(platformId)) {
    return false;
  }

  const capacitorPlatform = getCapacitorPlatform();
  if (capacitorPlatform === 'ios' || capacitorPlatform === 'android') {
    return true;
  }

  if (platform.IOS || platform.ANDROID) {
    return true;
  }

  if (typeof window !== 'undefined' && 'matchMedia' in window) {
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  }

  return false;
}

function getCapacitorPlatform(): string | null {
  try {
    return Capacitor.getPlatform?.() ?? null;
  } catch {
    return null;
  }
}

function applyTooltipMobilePatch(): void {
  if (TOOLTIP_PATCH_STATE.applied) {
    return;
  }

  const disabledDescriptor = Object.getOwnPropertyDescriptor(
    MatTooltip.prototype,
    'disabled'
  );
  if (!disabledDescriptor || !disabledDescriptor.set || !disabledDescriptor.get) {
    return;
  }

  TOOLTIP_PATCH_STATE.originalDisabled = disabledDescriptor;
  TOOLTIP_PATCH_STATE.originalShow = MatTooltip.prototype.show;
  TOOLTIP_PATCH_STATE.originalToggle = MatTooltip.prototype.toggle;

  Object.defineProperty(MatTooltip.prototype, 'disabled', {
    configurable: true,
    enumerable: disabledDescriptor.enumerable,
    get() {
      return true;
    },
    set(_value: boolean) {
      disabledDescriptor.set?.call(this, true);
    },
  });

  MatTooltip.prototype.show = function () {
    return;
  };
  MatTooltip.prototype.toggle = function () {
    return;
  };

  TOOLTIP_PATCH_STATE.applied = true;
}

export function resetTooltipMobilePatchForTests(): void {
  if (!TOOLTIP_PATCH_STATE.applied || !TOOLTIP_PATCH_STATE.originalDisabled) {
    return;
  }

  Object.defineProperty(
    MatTooltip.prototype,
    'disabled',
    TOOLTIP_PATCH_STATE.originalDisabled
  );
  if (TOOLTIP_PATCH_STATE.originalShow) {
    MatTooltip.prototype.show = TOOLTIP_PATCH_STATE.originalShow;
  }
  if (TOOLTIP_PATCH_STATE.originalToggle) {
    MatTooltip.prototype.toggle = TOOLTIP_PATCH_STATE.originalToggle;
  }

  TOOLTIP_PATCH_STATE.applied = false;
}

export function provideMobileTooltipDisable(): Provider[] {
  return [
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useFactory: () => {
        const isMobile = inject(TOOLTIP_MOBILE_DETECTOR);
        return () => {
          if (isMobile()) {
            applyTooltipMobilePatch();
          }
        };
      },
    },
  ];
}
